import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Link2, Copy, Trash2, FolderOpen, ListVideo } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@scio3/ui';
import { SHARE_LINK_ACCESS_TYPES, SHARE_LINK_ACCESS_ROLES } from '@scio3/shared-types';
import { DialogScrollBody } from '@/components/shared/DialogScrollBody';
import { useConfirm } from '@/stores/confirm-store';
import { useShareLinkDialogStore } from '../stores/share-link-dialog-store';
import { useShareLink } from '../hooks/useShareLink';
import { ShareLinkAccessSection } from './ShareLinkAccessSection';
import { ShareLinkEmailInput } from './ShareLinkEmailInput';
import { ShareLinkPeopleTable } from './ShareLinkPeopleTable';
import { ShareLinkAdvancedSection } from './ShareLinkAdvancedSection';
import { buildShareLinkUrl } from '../utils/share-link-url';
import { toast } from 'sonner';
import { useApolloClient } from '@apollo/client/react';

export function ShareLinkDialog() {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { isOpen, dialogContext, closeShareLinkDialog } = useShareLinkDialogStore();
  const { objectId, type, name } = dialogContext;

  const {
    shareLink,
    loading,
    creating,
    updating,
    updatingPeople,
    refetch,
    createShareLink,
    updateShareLink,
    updatePeopleRole,
    deleteShareLink,
  } = useShareLink(objectId);

  const confirm = useConfirm();

  const handleCreate = useCallback(async () => {
    if (!objectId || !type) return;
    try {
      await createShareLink({
        objectId,
        type,
        accessType: SHARE_LINK_ACCESS_TYPES.RESTRICTED,
        accessRole: SHARE_LINK_ACCESS_ROLES.VIEWER,
      });
      await refetch();
    } catch {
      toast.error(t('Failed to create share link'));
    }
  }, [objectId, type, createShareLink, refetch, t]);

  const handleCopyLink = useCallback(() => {
    if (!objectId || !type) return;
    const url = buildShareLinkUrl(type, objectId);
    navigator.clipboard.writeText(url);
    toast.success(t('Link copied to clipboard'));
  }, [objectId, type, t]);

  const handleUnshare = useCallback(async () => {
    if (!shareLink?._id) return;
    const confirmed = await confirm({
      title: t('Unshare Link'),
      description: t(
        'Are you sure you want to unshare this link? All shared users will lose access.',
      ),
      confirm: { label: t('Unshare'), variant: 'destructive' },
    });
    if (!confirmed) return;
    try {
      await deleteShareLink(shareLink._id);
      apolloClient.refetchQueries({ include: ['Playlists', 'AssetGroups'] });
      closeShareLinkDialog();
      toast.success(t('Share link removed'));
    } catch {
      toast.error(t('Failed to unshare link'));
    }
  }, [shareLink, deleteShareLink, apolloClient, closeShareLinkDialog, confirm, t]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        apolloClient.refetchQueries({ include: ['Playlists', 'AssetGroups'] });
        closeShareLinkDialog();
      }
    },
    [apolloClient, closeShareLinkDialog],
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        data-testid="share-link-dialog"
      >
        <DialogHeader variant="muted">
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-muted-foreground" />
            {t('Share Link')}
            {type === 'folder' && <FolderOpen className="h-4 w-4 text-muted-foreground" />}
            {type === 'playlist' && <ListVideo className="h-4 w-4 text-muted-foreground" />}
            {name && <span className="text-muted-foreground">— {name}</span>}
          </DialogTitle>
        </DialogHeader>

        <DialogScrollBody className="relative">
          {/* Loading overlay — preserves layout, no content shift */}
          {(loading || updating || updatingPeople) && shareLink && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {loading && !shareLink ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !shareLink ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <p className="text-sm text-muted-foreground">
                {t('No share link exists for this item.')}
              </p>
              <Button onClick={handleCreate} disabled={creating}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('Create Share Link')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* General Access */}
              <ShareLinkAccessSection shareLink={shareLink} onUpdate={updateShareLink} />

              <Separator />

              {/* Share via Email */}
              <ShareLinkEmailInput shareLink={shareLink} objectName={name ?? ''} />

              {/* People with Access */}
              {(shareLink.securities?.length ?? 0) > 0 && (
                <>
                  <Separator />
                  <ShareLinkPeopleTable
                    shareLink={shareLink}
                    onUpdatePeopleRole={updatePeopleRole}
                  />
                </>
              )}

              <Separator />

              {/* Advanced: Password & Expiration */}
              <ShareLinkAdvancedSection shareLink={shareLink} onUpdate={updateShareLink} />
            </div>
          )}
        </DialogScrollBody>

        {shareLink && (
          <DialogFooter>
            <Button
              data-testid="share-link-copy-link"
              variant="outline"
              onClick={handleCopyLink}
              className="mr-auto cursor-pointer"
            >
              <Copy className="mr-2 h-4 w-4" />
              {t('Copy Link')}
            </Button>

            <div className="flex items-center gap-2">
              <Button variant="destructive" onClick={handleUnshare} className="cursor-pointer">
                <Trash2 className="mr-2 h-4 w-4" />
                {t('Unshare')}
              </Button>
              <DialogClose asChild>
                <Button variant="ghost" className="cursor-pointer">
                  {t('Cancel')}
                </Button>
              </DialogClose>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
